import React, { forwardRef } from 'react'
import StyledTextInput from './StyledTextInput'
import { normalizeDecimalSeparator } from '../utils/decimalInput'

/**
 * ATL-1371 — the one input every monetary amount is typed into.
 *
 * Before this existed, each money field picked its own `keyboardType` and its own comma handling,
 * and most of them picked wrong: `number-pad` renders a DIGITS-ONLY keypad on both platforms (iOS
 * `UIKeyboardTypeNumberPad`, Android bare `TYPE_CLASS_NUMBER`), so a merchant literally could not
 * type 36.5 into the Money Transaction amount. `decimal-pad` adds the separator key on iOS and
 * `TYPE_NUMBER_FLAG_DECIMAL` on Android, and keeps every digit, so integer entry is unaffected.
 *
 * Two properties are load-bearing and both are one line away from being lost:
 *
 *   • `keyboardType` is applied AFTER `{...otherProps}`, so a call site cannot hand back the
 *     digits-only keypad by passing `keyboardType='number-pad'`. The policy is decided here, once.
 *   • `onChangeText` is wrapped so the value reaching a screen's state is ALWAYS dot-separated. The
 *     separator key follows the DEVICE LOCALE, so a ru/kk keypad emits a comma and `parseFloat`
 *     reads '36,5' as 36 — silently, with no NaN for a guard to catch. Pair this with
 *     `parseDecimalAmount` at the save, never with a bare `parseFloat`/`parseInt`.
 *
 * Everything else (placeholder, disabled, controlledInput, returnKeyType, selectTextOnFocus, refs)
 * is forwarded to `StyledTextInput` untouched.
 */
export default forwardRef(function MoneyTextInput({ onChangeText, ...otherProps }, ref) {
  return (
    <StyledTextInput
      ref={ref}
      {...otherProps}
      keyboardType='decimal-pad'
      onChangeText={onChangeText ? (text) => onChangeText(normalizeDecimalSeparator(text)) : undefined}
    />
  )
})
